import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Typography,
  Chip,
  Divider,
  Grow,
  CircularProgress,
  useTheme,
} from "@mui/material";
import GavelIcon from "@mui/icons-material/Gavel";
import Swal from "sweetalert2";
import { tokens } from "../../../theme";
import { executeAnyMutation } from "../../../data/DynamicServiceNew/jsondynamicservice";

const GrowTransition = React.forwardRef(function GrowTransition(props, ref) {
  return <Grow ref={ref} {...props} />;
});

const RegisterLienDialog = ({ open, row, onClose, onSuccess, base_url }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";

  const [referenceNo, setReferenceNo] = useState("");
  const [notes, setNotes] = useState("");
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const resetAndClose = () => {
    if (submitting) return;
    setReferenceNo("");
    setNotes("");
    setTouched(false);
    onClose();
  };

  const handleSubmit = async () => {
    setTouched(true);
    if (!referenceNo.trim()) return;

    setSubmitting(true);
    try {
      const payload = {
        id: row.LienId,
        status: "REGISTERED",
        referenceNo: referenceNo.trim(),
        notes: notes.trim(),
        assetId: row.AssetId,
      };

      const response = await executeAnyMutation(
        "updateLien",
        payload,
        "lien",
        false,
        base_url,
      );

      if (response?.error) {
        Swal.fire({ icon: "error", title: "Error!", text: response.error });
        return;
      }

      Swal.fire({
        icon: "success",
        title: "Lien Registered",
        text: `The lien on ${row.CustomerName || "the customer"}'s ${row.AssetTypeName || "asset"} has been registered.`,
      });
      onSuccess();
      resetAndClose();
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "An error occurred while registering the lien. Please try again later.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!row) return null;

  return (
    <Dialog
      open={open}
      onClose={resetAndClose}
      fullWidth
      maxWidth="sm"
      TransitionComponent={GrowTransition}
      keepMounted={false}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          background: "linear-gradient(135deg, #1766A2 0%, #2196F3 100%)",
          color: "#fff",
        }}
      >
        <GavelIcon />
        Register Lien
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        <Stack spacing={0.4} mb={1.5}>
          <Typography variant="caption" color={colors.grey[400]}>
            Customer
          </Typography>
          <Typography variant="body1" fontWeight={700}>
            {row.CustomerName || "—"}
            {row.CustomerPhone ? `  •  ${row.CustomerPhone}` : ""}
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mb={2}>
          {row.AssetTypeName && (
            <Chip label={row.AssetTypeName} size="small" color="primary" variant="outlined" />
          )}
          {row.AssetSubTypeName && (
            <Chip label={row.AssetSubTypeName} size="small" variant="outlined" />
          )}
          <Chip
            label={row.RegistryName || "No registry assigned"}
            size="small"
            color="info"
            variant="outlined"
          />
        </Stack>

        <Divider sx={{ mb: 2, borderColor: isDark ? "rgba(255,255,255,0.08)" : undefined }} />

        <TextField
          label="Lien Reference No."
          placeholder="e.g. NTSA/LIEN/2026/00123"
          fullWidth
          required
          autoFocus
          value={referenceNo}
          onChange={(e) => setReferenceNo(e.target.value)}
          error={touched && !referenceNo.trim()}
          helperText={
            touched && !referenceNo.trim()
              ? "Reference number is required"
              : "The registry's reference/filing number for this lien"
          }
          sx={{ mb: 2 }}
        />

        <TextField
          label="Notes"
          placeholder="Any additional filing notes..."
          fullWidth
          multiline
          minRows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </DialogContent>

      <DialogActions sx={{ p: 2.5, pt: 1 }}>
        <Button onClick={resetAndClose} disabled={submitting} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          startIcon={
            submitting ? <CircularProgress size={16} color="inherit" /> : <GavelIcon />
          }
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "8px",
            background: "linear-gradient(135deg, #1766A2 0%, #2196F3 100%)",
            "&:hover": {
              background: "linear-gradient(135deg, #145d92 0%, #1e88e5 100%)",
              boxShadow: "0 4px 12px rgba(23,102,162,0.4)",
            },
            transition: "all 0.18s ease",
          }}
        >
          {submitting ? "Registering..." : "Register Lien"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RegisterLienDialog;
